import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { motion } from "framer-motion";
import { Camera, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const QRScanner = ({ onScan, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanning = async () => {
    setError(null);
    setIsScanning(true);

    try {
      // Check for camera permission
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);

      // Initialize scanner
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Successfully scanned
          handleScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors (no QR found in frame)
        }
      );
    } catch (err) {
      console.error("Scanner error:", err);
      setIsScanning(false);
      
      if (err.name === "NotAllowedError") {
        setHasPermission(false);
        setError("Camera permission denied. Please allow camera access to scan QR codes.");
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else {
        setError("Could not start camera. Please try entering the code manually.");
      }
    }
  };

  const handleScanSuccess = async (decodedText) => {
    // Stop scanning
    if (html5QrCodeRef.current) {
      await html5QrCodeRef.current.stop().catch(() => {});
    }
    setIsScanning(false);

    // Extract join code from URL or use as-is
    let joinCode = decodedText;
    
    // Check if it's a URL with /join/ path
    if (decodedText.includes("/join/")) {
      const match = decodedText.match(/\/join\/([A-Za-z0-9]+)/);
      if (match) {
        joinCode = match[1].toUpperCase();
      }
    } else {
      // Assume it's just the code
      joinCode = decodedText.toUpperCase();
    }

    onScan(joinCode);
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      await html5QrCodeRef.current.stop().catch(() => {});
    }
    setIsScanning(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
    >
      {/* Close button */}
      <button
        onClick={() => {
          stopScanning();
          onClose();
        }}
        className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
        data-testid="close-scanner-btn"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      <div className="max-w-md w-full text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Scan QR Code</h2>
        <p className="text-[#E2D9F3] mb-6">Point your camera at the QR code shown by your facilitator</p>

        {/* Scanner container */}
        <div className="relative mb-6">
          <div 
            id="qr-reader" 
            ref={scannerRef}
            className="w-full rounded-2xl overflow-hidden bg-black/50"
            style={{ minHeight: isScanning ? "300px" : "0" }}
          />
          
          {!isScanning && !error && (
            <div className="glass-heavy rounded-2xl p-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center mx-auto mb-6">
                <Camera className="w-10 h-10 text-white" />
              </div>
              <Button
                onClick={startScanning}
                className="btn-action control-btn text-lg py-6 px-8"
                data-testid="start-scanner-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                Start Camera
              </Button>
            </div>
          )}

          {isScanning && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              {/* Scanning frame overlay */}
              <div className="w-64 h-64 border-2 border-[#CCFF00] rounded-2xl relative">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#CCFF00] rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#CCFF00] rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#CCFF00] rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#CCFF00] rounded-br-lg" />
                
                {/* Scanning line animation */}
                <motion.div
                  animate={{ y: [0, 240, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="absolute left-2 right-2 h-0.5 bg-[#CCFF00] shadow-[0_0_10px_#CCFF00]"
                />
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="glass-heavy rounded-xl p-4 mb-6 text-left">
            <p className="text-[#FF1744] mb-4">{error}</p>
            <Button
              onClick={startScanning}
              className="btn-ghost w-full"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Stop button when scanning */}
        {isScanning && (
          <Button
            onClick={stopScanning}
            variant="ghost"
            className="text-[#E2D9F3]"
          >
            Cancel Scanning
          </Button>
        )}

        {/* Manual entry hint */}
        <p className="text-sm text-[#6B5E7C] mt-4">
          Or enter the code manually on the previous screen
        </p>
      </div>
    </motion.div>
  );
};

export default QRScanner;
